import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import StationRingsIcon from '../../../static/icons/station_rings.svg';
import Eq from '../../components/lib/Eq/Eq';
import LastUpdated from '../../components/lib/LastUpdated/LastUpdated';
import * as Constants from '../../constants';
import { AriaRoles } from '../../constants/AccessibilityConstants';
import QA from '../../constants/QATags';
import { StationMessages } from '../../constants/StationConstants';
import Icon from '../Icon/Icon';

import './GridItem.scss';

export class GridItem extends React.Component {
  static propTypes = {
    isPlaying: PropTypes.bool,
    isCurrent: PropTypes.bool,
    isStation: PropTypes.bool,
    art: PropTypes.node.isRequired,
    badge: PropTypes.node,
    lastPlayed: PropTypes.string,
    firstLine: PropTypes.node.isRequired,
    secondLine: PropTypes.node,
    thirdLine: PropTypes.node,
    hoverOverlay: PropTypes.node,
    firstLineQaTag: PropTypes.string,
    secondLineQaTag: PropTypes.string,
    firstLineStyle: PropTypes.oneOf(['active', 'default']),
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    isHosted: PropTypes.bool,
  };

  static defaultProps = {
    firstLineStyle: 'default',
  };

  state = {
    isHovered: false,
  };

  renderThirdLine = () => {
    if (this.props.thirdLine) {
      return <div className='GridItem__metacontent__third'>{this.props.thirdLine}</div>;
    }
  };

  renderEqualizer = (isPlaying, qaTag) => {
    const classes = classNames('GridItem__art__nowPlayingOverlay', {
      'GridItem__art__nowPlayingOverlay--isPlaying': isPlaying,
    });
    return (
      <div className={classes}>
        <Eq isPlaying={isPlaying} qaTag={qaTag} />
      </div>
    );
  };

  onGridItemBlur = e => {
    // only handle the griditem blur event
    if (e.type === 'keydown' && e.target === e.currentTarget) {
      if (e.keyCode === Constants.App.KeyCodes.TAB && e.shiftKey) {
        this.onMouseLeave();
      }
      // if its not a reverse tab, do nothing
      return;
    }
  };

  onMouseEnter = () => {
    this.setState({
      isHovered: true,
      hadMouseEvent: true,
    });

    if (this.props.onMouseEnter) {
      this.props.onMouseEnter();
    }
  };

  onMouseLeave = () => {
    this.setState({
      isHovered: false,
      hadMouseEvent: true,
    });

    if (this.props.onMouseLeave) {
      this.props.onMouseLeave();
    }
  };

  /**
   * Captures the initial hover state, even when the component renders with the mouse already "over" it.
   * Note, this is an expensive event, so want to be sure to only use it once, then disable it.
   *
   */
  onMouseMove = () => {
    this.setState({
      isHovered: true,
      hadMouseEvent: true,
    });
  };

  /**
   * Creates an overlay.  Optionally adds it with a css transition.
   *
   * @param {object} overlayContent
   * @param {boolean} doTransition
   * @returns {object}
   */
  renderOverlay = (overlayContent, doTransition) => {
    const overlayClass = classNames({
      GridItem__overlay: true,
      'GridItem__overlay--transition': doTransition,
    });
    return (
      <div className={overlayClass}>
        {React.cloneElement(overlayContent, {
          onMouseLeave: this.onMouseLeave,
        })}
      </div>
    );
  };

  renderBadge = () => {
    if (!this.props.badge) return null;
    return <div className='GridItem__badge--alt'>{this.props.badge}</div>;
  };

  render() {
    let doOverlayTransition = true;
    let overlayContent = null;
    let overlay;
    let secondLine;

    // Will render the secondline and the overlay according to a set precedence...
    if (this.props.isPlaying) {
      secondLine = <FormattedMessage {...StationMessages.nowPlaying} />;
      overlayContent = !this.state.isHovered ? this.renderEqualizer(true, QA.ASPEO) : null;
      doOverlayTransition = false;
    } else if (this.props.isCurrent) {
      secondLine = <FormattedMessage {...StationMessages.nowPlaying} />;
      overlayContent = !this.state.isHovered ? this.renderEqualizer(false, QA.ASPEO) : null;
      doOverlayTransition = false;
    } else if (this.props.lastPlayed) {
      secondLine = <LastUpdated now={new Date()} lastPlayed={new Date(this.props.lastPlayed)} />;
    } else if (this.props.secondLine) {
      secondLine = this.props.secondLine;
    }

    if (this.state.isHovered) {
      overlayContent = this.props.hoverOverlay;
    }

    if (overlayContent) {
      overlay = this.renderOverlay(overlayContent, doOverlayTransition);
    }

    const accessibleLabel = this.props.secondLine
      ? this.props.firstLine && this.props.secondLine.props.children
        ? `${this.props.firstLine.props.children} ${
            this.props.secondLine.props.children[0]
              ? this.props.secondLine.props.children[0] + ' Listeners'
              : ' '
          }`
        : `${this.props.firstLine.props.children} ${this.props.secondLine.props.values.stationCount} Stations`
      : '';
    const firstLineClass = classNames({
      GridItem__metacontent__name: true,
      'GridItem__metacontent__name-default':
        !this.props.firstLineStyle || this.props.firstLineStyle === 'default',
      [`GridItem__metacontent__name-${this.props.firstLineStyle}`]: this.props.firstLineStyle,
      'GridItem__metacontent__name-hosted': this.props.isHosted,
    });

    return (
      <div className='GridItem'>
        <div
          className='GridItem__art'
          ref='gridItemArt'
          aria-label={accessibleLabel}
          role={this.props.isStation ? AriaRoles.MENU : AriaRoles.PRESENTATION}
          onFocus={this.onMouseEnter}
          onKeyDown={this.onGridItemBlur}
          onMouseMove={this.state.hadMouseEvent ? null : this.onMouseMove}
          onMouseEnter={this.onMouseEnter}
          onMouseLeave={this.onMouseLeave}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={'0'}
        >
          {this.props.art}
          {this.props.isStation && (
            <div className='GridItem__rings'>
              <Icon className='stationRings' glyph={StationRingsIcon} />
            </div>
          )}
          {overlay}
          {this.renderBadge()}
        </div>
        <div className='GridItem__metacontent'>
          <div className={firstLineClass} data-qa={this.props.firstLineQaTag}>
            {this.props.firstLine}
          </div>
          <div className='GridItem__metacontent__second' data-qa={this.props.secondLineQaTag}>
            {secondLine}
          </div>
          {this.renderThirdLine()}
        </div>
      </div>
    );
  }
}

export default GridItem;
